import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {toast} from 'react-toastify';

import {downloadMarkingCOdesByTINS} from 'api';
import {DropdownMenu} from 'components//ui-kit/DropdownMenu';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {EndlessList} from 'components/ui-kit/EndlessList';
import {Table} from 'components/ui-kit/Table';
import {commonDataState$, fetchLabelsEvent, Gate, productsTableState$} from 'models/orders/detail';
import {breakpoint} from 'theme';
import {TRANSPORT_PACKAGE_TYPES} from 'types/gtins';
import {
    OrderDetailProductsFormattedRow,
    OrderDetailProductsRaw,
    OrdersDetailProductsAssessors,
} from 'types/orders';
import {useMatchMedia} from 'utils/custom-hooks';
import {handleNil} from 'utils/handleNil';

import {FlexRowElement, ListRow, Text} from './styled';

export function convertData(
    data: OrderDetailProductsRaw[] | undefined,
): OrderDetailProductsFormattedRow[] {
    if (!data) {
        return [];
    }

    return data.map((elem) => ({
        [OrdersDetailProductsAssessors.productId]: handleNil(elem?.productId),
        [OrdersDetailProductsAssessors.productName]: handleNil(elem?.productName),
        [OrdersDetailProductsAssessors.quantity]: handleNil(elem?.quantity),
        [OrdersDetailProductsAssessors.productGroupCode]: TRANSPORT_PACKAGE_TYPES.includes(
            elem.packageType,
        )
            ? '—'
            : handleNil(elem.productGroupCode, (value) => <ProductGroupIcon value={value} label />),
        [OrdersDetailProductsAssessors.availableQuantity]: handleNil(elem?.availableQuantity),
        [OrdersDetailProductsAssessors.gtin]: handleNil(elem?.gtin),
    }));
}

export const Products = () => {
    const {id} = useStore(Gate.state);

    const onDownload = async (currentGtin: string, ext = 'csv') => {
        try {
            if (!currentGtin) throw Error('GTIN is required');
            const response = await downloadMarkingCOdesByTINS({gtin: currentGtin, orderId: id});
            saveAs(response, `marking_codes_${id}_${currentGtin}.${ext}`);
        } catch (err) {
            console.error('Error:', err);
            toast.error(err);
        }
    };

    const getConfig = (t: TFunction, status?: String) => [
        // {
        //     Header: t('pages.orders.detail.productsTab.productId'),
        //     accessor: OrdersDetailProductsAssessors.productId,
        //     width: 200,
        //     disableSortBy: true,
        // },
        {
            Header: t('pages.orders.productGroup'),
            accessor: OrdersDetailProductsAssessors.productGroupCode,
            width: 150,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.detail.productsTab.productName'),
            accessor: OrdersDetailProductsAssessors.productName,
            width: 170,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.detail.productsTab.quantity'),
            accessor: OrdersDetailProductsAssessors.quantity,
            width: 150,
            disableSortBy: true,
        },
        // {
        //     Header: t('pages.orders.detail.productsTab.availableQuantity'),
        //     accessor: OrdersDetailProductsAssessors.availableQuantity,
        //     width: 150,
        //     disableSortBy: true,
        // },
        {
            Header: t('pages.orders.detail.productsTab.gtin'),
            accessor: OrdersDetailProductsAssessors.gtin,
            width: 150,
            disableSortBy: true,
        },
        {
            Header: '',
            accessor: 'action-print',
            Cell: ({row}: {row: {original: OrderDetailProductsFormattedRow}}) => (
                <>
                    {
                        <DropdownMenu label="Print" width={109}>
                            <DropdownMenu.List>
                                {status !== 'DECLINED' &&
                                    parseInt(row.original.availableQuantity, 10) > 0 && (
                                        <DropdownMenu.Item
                                            onSelect={() =>
                                                fetchLabelsEvent({
                                                    orderId: id,
                                                    gtin: row.original.gtin,
                                                    template: 'white',
                                                    quantity: +row.original.availableQuantity,
                                                    format: 'PDF',
                                                })
                                            }
                                        >
                                            PDF
                                        </DropdownMenu.Item>
                                    )}
                                <DropdownMenu.Item
                                    onSelect={() => onDownload(row.original.gtin, 'xls')}
                                >
                                    XLS
                                </DropdownMenu.Item>
                                <DropdownMenu.Item onSelect={() => onDownload(row.original.gtin)}>
                                    CSV
                                </DropdownMenu.Item>
                            </DropdownMenu.List>
                        </DropdownMenu>
                        // <Button
                        //     icon={<Icon width={14} source={PrintIcon} />}
                        //     variant={ButtonVariant.Text}
                        //     onClick={() => currentGtinChanged(row.original.gtin)}
                        // />
                    }
                </>
            ),
            disableSortBy: true,
        },
    ];

    const {t} = useTranslation();
    const {data, isLoading} = useStore(productsTableState$);
    const {data: commonData} = useStore(commonDataState$);
    const status = commonData?.status;
    const columns = React.useMemo(() => getConfig(t, status), [t, status]);
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);

    const mappedData = React.useMemo(() => convertData(data), [data, t]);

    return (
        <div>
            {!isMobile && (
                <Table
                    columns={columns}
                    data={mappedData}
                    totalRowsCount={data.length}
                    isLoading={isLoading}
                    hideSetting
                />
            )}

            {isMobile && (
                <EndlessList<OrderDetailProductsFormattedRow>
                    rows={mappedData}
                    totalRowsCount={data.length}
                    renderRow={(row: OrderDetailProductsFormattedRow) => (
                        <ListRow>
                            <FlexRowElement>
                                <Text isGray>{row[OrdersDetailProductsAssessors.productName]}</Text>
                            </FlexRowElement>
                            <FlexRowElement>
                                <Text>{row[OrdersDetailProductsAssessors.productId]}</Text>
                            </FlexRowElement>
                            <FlexRowElement>
                                <Text isGray>{row[OrdersDetailProductsAssessors.gtin]}</Text>
                                <Text isGray>{row[OrdersDetailProductsAssessors.quantity]}</Text>
                            </FlexRowElement>
                        </ListRow>
                    )}
                    config={columns}
                />
            )}
        </div>
    );
};
